// =============================================================================
// SelfUserCard — popover for your OWN user, anchored to the bottom-left bar.
// =============================================================================
// Opens upward from the bottom user-bar. Shows your identity plus quick actions
// (View profile / Edit profile) and a Notifications panel (wall posts, etc.)
// backed by useNotifications(). Portaled to document.body like UserPopover, with
// the same resize-aware repositioning.
// =============================================================================

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Avatar, Button, Icon, Spinner } from './ui';
import { useApp } from '../store';
import { useNotifications } from '../hooks';
import type { Notification } from '../types';

export function SelfUserCard({ anchor, onClose }: { anchor: HTMLElement | null; onClose: () => void }) {
  const navigate = useNavigate();
  const me = useApp((s) => s.user)!;
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Anchor the card ABOVE the bottom bar (opens upward), left-aligned, clamped
  // to the viewport. Recomputed on resize / async content growth like UserPopover.
  const reposition = useCallback(() => {
    if (!anchor || !cardRef.current) return;
    const a = anchor.getBoundingClientRect();
    const box = cardRef.current.getBoundingClientRect();
    const margin = 8;
    let left = a.left;
    if (left + box.width + margin > window.innerWidth) left = window.innerWidth - box.width - margin;
    if (left < margin) left = margin;
    let top = a.top - box.height - margin; // above the bar
    if (top < margin) top = margin;
    setPos({ left, top });
  }, [anchor]);

  useLayoutEffect(() => reposition(), [reposition]);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => reposition());
    ro.observe(el);
    window.addEventListener('resize', reposition);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, [reposition]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (cardRef.current?.contains(e.target as Node)) return;
      if (anchor?.contains(e.target as Node)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-label="Your account"
      className="fixed z-60 hybrid-card w-72 select-text overflow-hidden"
      style={{
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
        maxHeight: 'calc(100vh - 16px)',
      }}
    >
      <div className="bg-surface p-3 space-y-3">
        <div className="flex items-center gap-3">
          <Avatar name={me.displayName || me.username} url={me.avatarUrl} size={44} />
          <div className="min-w-0 flex-1">
            <div className="font-bold text-text truncate">{me.displayName || me.username}</div>
            <div className="text-xs text-text-muted truncate">@{me.username}</div>
            {me.isInvisible && <div className="text-[10px] text-text-subtle">Invisible</div>}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={() => go(`/u/${encodeURIComponent(me.username)}`)} className="flex-1 justify-center">
            <Icon name="user" size={12} /> View profile
          </Button>
          <Button variant="primary" onClick={() => go('/settings/profile')} className="flex-1 justify-center">
            <Icon name="settings" size={12} /> Edit profile
          </Button>
        </div>

        <NotificationsPanel onNavigate={go} selfUsername={me.username} />
      </div>
    </div>,
    document.body
  );
}

// Bottom-bar trigger: the clickable avatar + name that opens SelfUserCard, with
// an unread-notification dot on the avatar. Drop-in for the old static block.
export function SelfBarTrigger() {
  const me = useApp((s) => s.user);
  const { unreadCount } = useNotifications();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  if (!me) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex-1 min-w-0 flex items-center gap-2 rounded-sm px-1 py-0.5 -mx-1 hover:bg-elevated transition-colors text-left"
        title={me.displayName ? `${me.displayName} (@${me.username})` : `@${me.username}`}
      >
        <div className="relative shrink-0">
          <Avatar
            name={me.displayName || me.username}
            url={me.avatarUrl}
            size={28}
            status={me.isInvisible ? 'offline' : 'online'}
          />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 bg-danger text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none border border-elevated">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate text-text leading-tight">
            {me.displayName || me.username}
          </div>
          {me.isInvisible && (
            <div className="text-[10px] text-text-subtle leading-tight">Invisible</div>
          )}
        </div>
      </button>
      {open && <SelfUserCard anchor={triggerRef.current} onClose={() => setOpen(false)} />}
    </>
  );
}

function NotificationsPanel({
  onNavigate,
  selfUsername,
}: {
  onNavigate: (path: string) => void;
  selfUsername: string;
}) {
  const { notifications, unreadCount, loading, markRead } = useNotifications();

  // Mark everything read shortly after the panel is shown.
  useEffect(() => {
    if (unreadCount > 0) {
      const t = setTimeout(() => void markRead(), 1200);
      return () => clearTimeout(t);
    }
  }, [unreadCount, markRead]);

  function clickNotification(n: Notification) {
    void markRead(n.id);
    // Message-backed types carry a ready client route (built server-side,
    // where the server id + channel name are already loaded) with the ?m=
    // message anchor TextChannelView jumps to on load.
    if (n.link) {
      onNavigate(n.link);
      return;
    }
    // guestbook_post → someone wrote on YOUR wall, so open your own profile.
    onNavigate(`/u/${encodeURIComponent(selfUsername)}`);
  }

  return (
    <div className="border-t border-border-soft pt-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon name="bell" size={12} className="text-text-muted" />
        <span className="text-[10px] uppercase tracking-wider text-text-subtle font-bold">Notifications</span>
        {unreadCount > 0 && (
          <span className="bg-danger text-white text-[9px] font-bold px-1.5 rounded-full leading-[1.4]">
            {unreadCount}
          </span>
        )}
      </div>
      <div className="max-h-56 overflow-y-auto -mx-1 px-1">
        {loading ? (
          <div className="flex justify-center py-3 text-text-muted">
            <Spinner size={14} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-xs text-text-subtle py-2">No notifications yet.</div>
        ) : (
          <ul className="space-y-1">
            {notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => clickNotification(n)}
                  className={`w-full text-left flex items-start gap-2 rounded px-1.5 py-1.5 hover:bg-elevated transition-colors ${
                    n.read ? '' : 'bg-accent/5'
                  }`}
                >
                  <Avatar name={n.actor.displayName || n.actor.username} url={n.actor.avatarUrl} size={26} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-text leading-snug">
                      <span className="font-semibold">{n.actor.displayName || n.actor.username}</span>{' '}
                      {notifLabel(n.type)}
                    </div>
                    <div className="text-[10px] text-text-subtle">{relativeTime(n.createdAt)}</div>
                  </div>
                  {!n.read && <span className="mt-1 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function notifLabel(type: Notification['type']): string {
  switch (type) {
    case 'guestbook_post':
      return 'wrote on your wall';
    case 'mention':
      return 'mentioned you';
    case 'reply':
      return 'replied to you';
    default:
      return 'sent you a notification';
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
